//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useMemo } from 'react';
import NoDataMessage from '@/components/_common/NoDataMessage';
import { FileFormat } from '@/constants/constants';
import { selectJob } from '@/redux/selectors/jobSelectors';
import { selectAllLanguages, selectFileTypeGroupings, selectSubtitlesLanguages } from '@/redux/selectors/constantsSelectors';
import styled from '@emotion/styled';
import { Grid, darken, lighten, useTheme } from '@mui/material';
import DeliverableFile from './v2/DeliverableFile';
import DeliverableSection from './v2/DeliverableSection';
import DeliverableTrackTable from './v2/DeliverableTrackTable';
import { IconDictionaryType } from '@/components/_common/PolarisIcon';
import SimpleLabelValue from '@/components/_common/SimpleLabelValue';
import { useAppSelector } from '@/redux/hooks';
import { titleCase } from '@/_utils/utils';

type IconType = Extract<keyof IconDictionaryType, string>;
type DeliverablesSettings = {
  label: string;
  icon: IconType;
  color: string;
  data: any[];
  renderDropdown: (files: any[], color: string, icon: string) => React.ReactNode;
};

const sortFiles = (files: any[]) => {
  return files[0]?.track_num
    ? files.sort((a: any, b: any) => {
        if (a.track_num < b.track_num) return -1;
        if (a.track_num > b.track_num) return 1;
        return 0;
      })
    : files;
};

const renderMediaList = (key: string, files: any[], labelKey: string, color: string) => {
  return files.length > 0 ? (
    sortFiles(files).map((file: any, i: number) => (
      <Highlighted color={color} key={`${key}-${i}`}>
        <span>{i + 1}:</span>
        <span>{file[labelKey] || '-'}</span>
      </Highlighted>
    ))
  ) : (
    <div className="unformatted">-</div>
  );
};

const DeliverablesV2 = () => {
  const theme = useTheme();
  const jobData = useAppSelector(selectJob);
  const SubtitlesLanguages = useAppSelector(selectSubtitlesLanguages);
  const AllLanguages = useAppSelector(selectAllLanguages);
  const fileTypeGroupings = useAppSelector(selectFileTypeGroupings);

  const renderAndIngestSkipped = useMemo(() => {
    const skipped = jobData?.tasks?.reduce(
      (acc: any, task: any) => {
        if (task.status === 'skipped') {
          if (task.name === 'render') acc.render = true;
          if (task.name === 'ingest') acc.ingest = true;
        }
        return acc;
      },
      { render: false, ingest: false }
    );
    return skipped?.render && skipped?.ingest;
  }, [jobData]);

  const settings: Record<'media' | 'audio' | 'subtitle' | 'image' | 'other', DeliverablesSettings> = {
    media: {
      label: 'Media',
      icon: 'video',
      color: theme.palette.info.main,
      data: (jobData.deliverables || []).filter(
        (item: any) => fileTypeGroupings.video.includes(item.type as FileFormat) || fileTypeGroupings.video.includes(item.file_type as FileFormat)
      ),
      renderDropdown: (files: any[], color: string, icon: string) => {
        return files.map((file: any, i: number) => (
          <DeliverableFile key={`deliverable-${i}`} icon={icon} color={color} fileName={file.name}>
            {(file.type === 'mux' || file.type === 'mxf') && (
              <TopSection bg={color}>
                <DataLine>
                  <Label full>Audio</Label>
                  <HighlightList>
                    {file.audio && file.audio?.length > 0 ? renderMediaList('audio', file.audio || [], 'config', color) : '-'}
                  </HighlightList>
                </DataLine>
                <DataLine>
                  <Label full>Subtitle</Label>
                  <HighlightList>
                    {file.subtitle && file.subtitle.length > 0 ? renderMediaList('subtitle', file.subtitle || [], 'name', color) : '-'}
                  </HighlightList>
                </DataLine>
              </TopSection>
            )}
            <BottomSection>
              <Row>
                <DataHalfLine bg={color}>
                  <Label>Resolution</Label>
                  <span>{file.media_resolution || '-'}</span>
                </DataHalfLine>
                <DataHalfLine bg={color}>
                  <Label>Bitrate</Label>
                  <span>{file.bitrate || '-'}</span>
                </DataHalfLine>
              </Row>
              <Row>
                <DataHalfLine bg={color}>
                  <Label>Aspect Ratio</Label>
                  <span>{file.aspect_ratio || '-'}</span>
                </DataHalfLine>
                <DataHalfLine bg={color}>
                  <Label>Encoding</Label>
                  <span>{file.encoding || '-'}</span>
                </DataHalfLine>
              </Row>
            </BottomSection>
          </DeliverableFile>
        ));
      }
    },

    audio: {
      label: 'Audio',
      icon: 'music',
      color: theme.palette.warning.main,
      data: (jobData.deliverables || []).filter(
        (item: any) => fileTypeGroupings.audio.includes(item.type as FileFormat) || fileTypeGroupings.audio.includes(item.file_type as FileFormat)
      ),
      renderDropdown: (files: any[], color: string, icon: string) => {
        return files.map((audioFile: any, i: number) => (
          <DeliverableFile key={`${audioFile.name}-${i + 1}`} icon={icon} color={color} fileName={audioFile.name}>
            <DeliverableTrackTable columns={['track', 'type', 'encoding', 'language']} data={audioFile.audio ?? []} color={color} />
          </DeliverableFile>
        ));
      }
    },

    subtitle: {
      label: 'Subtitle(s)',
      icon: 'subtitle',
      color: theme.palette.grey[500],
      data: (jobData.deliverables || []).filter(
        (item: any) =>
          fileTypeGroupings.subtitle.includes(item.type as FileFormat) || fileTypeGroupings.subtitle.includes(item.file_type as FileFormat)
      ),
      renderDropdown: (files: any[], color: string, icon: string) => {
        return files.map((subtitleFile: any, i: number) => {
          const lang = SubtitlesLanguages?.find((lang) => [subtitleFile.language, subtitleFile.language?.replace('-', '_')].includes(lang.code));
          return (
            <DeliverableFile key={`${subtitleFile.name}-${i + 1}`} icon={icon} color={color} fileName={subtitleFile.name}>
              <BottomSection>
                <Row>
                  <DataHalfLine bg={color}>
                    <Label>Language</Label>
                    <span>{SimpleLabelValue({ label: lang?.label, value: lang?.code, fallback: subtitleFile.language || '-' })}</span>
                  </DataHalfLine>
                  <DataHalfLine bg={color}>
                    <Label>Format</Label>
                    <span>{subtitleFile.file_type || '-'}</span>
                  </DataHalfLine>
                </Row>
              </BottomSection>
              {/* <DeliverableTrackTable columns={['track', 'type', 'language']} data={subtitleFile} color={color} /> */}
            </DeliverableFile>
          );
        });
      }
    },

    image: {
      label: 'Image(s)',
      icon: 'image',
      color: theme.palette.teal.main,
      data: (jobData.deliverables || []).filter(
        (item: any) => fileTypeGroupings.image.includes(item.type as FileFormat) || fileTypeGroupings.image.includes(item.file_type as FileFormat)
      ),
      renderDropdown: (files: any[], color: string, icon: string) => {
        return files.map((imageFile: any, i: number) => (
          <DeliverableFile key={`${imageFile.name}-${i + 1}`} icon={icon} color={color} fileName={imageFile.name}>
            <BottomSection>
              <Row>
                <DataHalfLine bg={color}>
                  <Label>Type</Label>
                  <span>{imageFile.sub_type || '-'}</span>
                </DataHalfLine>
                <DataHalfLine bg={color}>
                  <Label>File Size</Label>
                  <span>{imageFile.file_size || '-'}</span>
                </DataHalfLine>
              </Row>
            </BottomSection>
          </DeliverableFile>
        ));
      }
    },

    other: {
      label: 'Other',
      icon: 'file',
      color: theme.palette.lavender.main,
      data: (jobData.deliverables || []).filter(
        (item: any) => fileTypeGroupings.other.includes(item.type as FileFormat) || fileTypeGroupings.other.includes(item.file_type as FileFormat)
      ),
      renderDropdown: (files: any[], color: string, icon: string) => {
        return files.map((otherFile: any, i: number) => {
          const lang = AllLanguages?.find((lang) => [otherFile.language, otherFile.language?.replace('-', '_')].includes(lang.code));
          return (
            <DeliverableFile key={`${otherFile.name}-${i + 1}`} icon={icon} color={color} fileName={otherFile.name}>
              <BottomSection>
                <Row>
                  {lang && (
                    <DataHalfLine bg={color}>
                      <Label>Language</Label>
                      <span>{SimpleLabelValue({ label: lang?.label, value: lang?.code, fallback: otherFile.language || '-' })}</span>
                    </DataHalfLine>
                  )}
                  <DataHalfLine bg={color}>
                    <Label>Type</Label>
                    <span>{titleCase(otherFile.sub_type) || '-'}</span>
                  </DataHalfLine>
                </Row>
              </BottomSection>
            </DeliverableFile>
          );
        });
      }
    }
  };

  if (!jobData?.deliverables?.length)
    return <NoDataMessage message={renderAndIngestSkipped ? 'No deliverables expected for this job.' : 'Awaiting Deliverable Information.'} />;
  return (
    <Wrapper>
      {Object.keys(settings).map((key) => {
        const { label, icon, color, data, renderDropdown } = settings[key as keyof typeof settings];
        return (
          data.length > 0 && (
            <DeliverableSection title={label} count={data.length} color={color} key={`section-${label.toLowerCase()}`}>
              {renderDropdown(data, color, icon)}
            </DeliverableSection>
          )
        );
      })}
    </Wrapper>
  );
};

export default DeliverablesV2;

const Wrapper = styled.div`
  & > :last-child {
    margin-bottom: 0px;
  }
`;

const TopSection = styled.div<{ bg: string }>`
  background: ${({ theme, bg }) => (theme.palette.mode === 'light' ? lighten(bg, 0.8) : darken(bg, 0.6))};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  margin-top: ${({ theme }) => theme.spacing(2)};

  > div:last-of-type {
    border-bottom: none;
  }
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.spacing(1.5)};
  row-gap: ${({ theme }) => theme.spacing(1.5)};
`;

const DataLine = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  display: flex;
  flex-direction: row;
  padding: ${({ theme }) => theme.spacing(1.5)};
  align-items: center;
`;

const Label = styled.span<{ full?: boolean }>`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  width: ${({ full }) => (full ? '75px' : '100%')};
  opacity: 0.7;
  padding-right: ${({ theme }) => theme.spacing(2)};
`;

export const Highlighted = styled.div<{ color: string }>`
  background: ${({ color }) => color};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme, color }) => theme.palette.getContrastText(color)};
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  border-radius: 2px;
  width: fit-content;

  > span:first-of-type {
    padding-right: ${({ theme }) => theme.spacing(1)};
  }
`;

const HighlightList = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing(1)};

  .unformatted {
    background: none;
    font-style: italic;
    font-weight: ${({ theme }) => theme.typography.fontWeightRegular};
  }
`;

const DataHalfLine = styled(Grid)<{ bg: string }>`
  background: ${({ theme, bg }) => (theme.palette.mode === 'light' ? lighten(bg, 0.8) : darken(bg, 0.6))};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(1.5)};
  flex-grow: 1;
  flex-basis: 0;
`;

const Row = styled.div`
  display: flex;
  column-gap: ${({ theme }) => theme.spacing(1.5)};
  flex-direction: row;
`;
